import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, enableIndexedDbPersistence, initializeFirestore } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// Firebase configuration
// In production, these should be in environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'your-api-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'your-auth-domain',
  // Ensure the emulator uses the demo project id consistently
  projectId: (import.meta.env.DEV ? 'demo-masonic-tools' : import.meta.env.VITE_FIREBASE_PROJECT_ID) || 'masonic-tools',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'your-storage-bucket',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || 'your-sender-id',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || 'your-app-id',
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Check if we should use emulators FIRST
const isDevelopment = import.meta.env.DEV;
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true' || isDevelopment;

// Initialize services
export const auth = getAuth(app);
export const db = useEmulators
  ? initializeFirestore(app, {
      experimentalAutoDetectLongPolling: true,
    })
  : getFirestore(app);
// Always bind Functions to europe-west2 to match deployed region (emulator supported)
export const functions = getFunctions(app, 'europe-west2');
export const storage = getStorage(app);

// Connect to emulators in development mode
if (useEmulators) {
  try {
    // Use "localhost" for all emulator hosts to match firebase.json emulators.*.host and the Vite dev
    // origin (e.g. http://localhost:3004). Calling Functions at 127.0.0.1:5001 from localhost:* fails
    // CORS preflight because the browser treats localhost and 127.0.0.1 as different origins.
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8090);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    connectStorageEmulator(storage, 'localhost', 9199);
    
    console.log('🔥 Connected to Firebase Emulators');
    console.log('📊 Emulator UI: http://localhost:4000');
  } catch (error) {
    console.error('❌ Could not connect to emulators:', error);
    console.warn('Make sure emulators are running: firebase emulators:start');
  }
}

// Enable offline persistence (only in production, not with emulators)
if (!isDevelopment && import.meta.env.VITE_USE_EMULATORS !== 'true') {
  enableIndexedDbPersistence(db).catch((err) => {
    if (err.code === 'failed-precondition') {
      console.warn('Firebase persistence: Multiple tabs open');
    } else if (err.code === 'unimplemented') {
      console.warn('Firebase persistence not supported');
    }
  });
}

export default app;

